import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, putStyle } from '../core/api';
import { useApp } from '../App';
import type { Style } from '../core/types';

interface Card {
  style: Style;
  kicker: string;
  name: string;
  desc: string;
  cls: string;
  ready: boolean;
}

const CARDS: Card[] = [
  { style: 'simple', kicker: 'STYLE 01', name: 'THIS PLACE IS SIMPLE', desc: 'No map, no walking. A list of what you owe.', cls: 'kis', ready: true },
  { style: 'prison', kicker: 'STYLE 02', name: 'THIS PLACE IS A PRISON', desc: 'Walk the wing. Somebody is going in the hole.', cls: 'pr', ready: true },
  { style: 'abc', kicker: 'STYLE 03', name: 'THIS PLACE IS A MEMORY', desc: 'The ABC on Sauchiehall Street, back when it was still there.', cls: 'abc', ready: true },
  { style: 'rail', kicker: 'STYLE 04', name: 'THIS PLACE IS DELAYED', desc: 'We are sorry to announce this venue is subject to disruption.', cls: 'rail', ready: true },
];

/** The venue picker (design/select.html). Only the look and the words change —
 *  points, guesses and the veto are identical, and one league table underneath. */
export function StyleSelect() {
  const { style: current } = useApp();
  const nav = useNavigate();
  const [busy, setBusy] = useState<Style | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function pick(s: Style, ready: boolean) {
    if (!ready) return;
    if (s === current) {
      nav('/'); // already on this style — just head back into the app
      return;
    }
    setBusy(s);
    setErr(null);
    try {
      await putStyle(s);
      window.location.href = '/'; // full re-boot so the whole app re-themes
    } catch (e) {
      setErr((e as ApiError).message || 'Could not switch style.');
      setBusy(null);
    }
  }

  return (
    <div className="styleselect">
      <header className="ss-top">
        <h1>SAME PLACE.<br />PICK YOUR STYLE.</h1>
        <p>YOUR SCORES FOLLOW YOU EITHER WAY</p>
      </header>

      <div className="ss-cards">
        {CARDS.map((c) => (
          <button
            key={c.style}
            className={`ss-card ${c.cls}${c.style === current ? ' here' : ''}${!c.ready ? ' soon' : ''}`}
            disabled={busy !== null || !c.ready}
            onClick={() => pick(c.style, c.ready)}
          >
            <span className="ss-body">
              <span className="ss-kicker">{c.kicker}{c.style === current ? ' · HERE' : ''}</span>
              <span className="ss-name">{c.name}</span>
              <span className="ss-desc">{c.desc}</span>
            </span>
            <span className="ss-go">
              {busy === c.style ? '…' : !c.ready ? 'SOON' : '▶'}
            </span>
          </button>
        ))}
      </div>

      {err ? <p className="ss-err">{err}</p> : null}
      <nav className="ss-links">
        <button onClick={() => nav('/intro')}>WHAT IS THIS?</button>
        <button onClick={() => nav('/rules')}>THE RULES</button>
      </nav>
    </div>
  );
}
